import { Switch as SwitchPrimitive } from '@base-ui/react/switch'

import { cn } from '@safe-global/views/utils/cn'

/**
 * Switch Component
 *
 * Toggle switch (on/off control).
 *
 * @see https://ui.shadcn.com/docs/components/base/switch
 *
 * @example
 * ```tsx
 * <Field orientation="horizontal">
 *   <Switch id="airplane" />
 *   <FieldLabel htmlFor="airplane">Airplane Mode</FieldLabel>
 * </Field>
 * ```
 *
 * @remarks
 * Key Props:
 * - `defaultChecked`: uncontrolled initial state
 * - `checked`, `onCheckedChange`: controlled state
 * - `size`: 'sm' | 'default'
 * - `disabled`: disables interaction
 */

function Switch({
  className,
  size = 'default',
  ...props
}: SwitchPrimitive.Root.Props & {
  size?: 'sm' | 'default'
}) {
  return (
    <SwitchPrimitive.Root
      data-slot="switch"
      data-size={size}
      className={cn(
        'data-checked:bg-primary data-unchecked:bg-muted data-unchecked:border-border focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 dark:data-unchecked:bg-input/80 shrink-0 rounded-full border border-transparent shadow-xs focus-visible:ring-[3px] aria-invalid:ring-[3px] data-[size=default]:h-[18.4px] data-[size=default]:w-[32px] data-[size=sm]:h-[14px] data-[size=sm]:w-[24px] peer group/switch relative inline-flex items-center transition-all outline-none after:absolute after:-inset-x-3 after:-inset-y-2 data-disabled:cursor-not-allowed data-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="data-unchecked:bg-foreground data-checked:bg-background dark:data-unchecked:bg-foreground dark:data-checked:bg-primary-foreground rounded-full group-data-[size=default]/switch:size-4 group-data-[size=sm]/switch:size-3 group-data-[size=default]/switch:data-checked:translate-x-[calc(100%-2px)] group-data-[size=sm]/switch:data-checked:translate-x-[calc(100%-2px)] group-data-[size=default]/switch:data-unchecked:translate-x-0 group-data-[size=sm]/switch:data-unchecked:translate-x-0 pointer-events-none block ring-0 transition-transform"
      />
    </SwitchPrimitive.Root>
  )
}

export { Switch }
